import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from "react-router-dom";

import Paginator from '../../components/ui/Paginator';
import Message from '../../components/ui/Message';
import { MerchantsContext } from '../../context/merchants/MerchantContext';
import { Merchant } from '../../models/interfaces';
import { AuthContext } from '../../context/auth';
import { canFilterMerchants, isPlatformUser } from '../../utils/access';
import { SWIFTPAY_BANK_ID } from '../../utils/appConfig';


type PendingAction = { type: 'suspend' | 'delete'; merchant: Merchant } | null;

const getCompanyType = (companyTypeId: number) => {
    switch (companyTypeId) {
        case 1:
            return "Restaurante";
        case 2:
            return "Comercio";
        case 3:
            return "Delivery";
        case 4:
            return "POS";
        default:
            return "";
    }
}

export const MerchantsPage = () => {
    const [buscar, setBuscar] = useState('');
    const [pageActual, setPageActual] = useState<number>(1);
    const [isLoadingPage, setIsLoadingPage] = useState(false);
    const { merchants, totalPage, loadMerchants, deleteMerchant, suspendMerchant, activateMerchant, generateTokenEcom } = useContext(MerchantsContext);

    const [errorMessage, setErrorMessage] = useState('');
    // Resultado del cobro de la suscripción al reactivar un comercio suspendido por falta de pago
    const [activationNotice, setActivationNotice] = useState<{ ok: boolean; message: string } | null>(null);
    const [tokenEcom, setTokenEcom] = useState('');
    const [currentMercId, setCurrentMercId] = useState(-1);
    const [pendingAction, setPendingAction] = useState<PendingAction>(null);
    const [toast, setToast] = useState("");

    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const isPlatform = isPlatformUser(user);
    // Terminales: plataforma o usuarios del banco, solo en comercios de Swiftpay
    const canManageTerminals = canFilterMerchants(user);

    useEffect(() => {
        setIsLoadingPage(true);
        loadMerchants(pageActual - 1).finally(() => setIsLoadingPage(false));
    }, [pageActual]);

    const filteredData = useMemo(() => {
        const term = buscar.trim().toLowerCase();
        if (!term) return merchants;
        return merchants.filter((merchant) =>
            [merchant.name, merchant.legalId, merchant.topId, merchant.topIdDol, merchant.bank?.description, merchant.parent?.name]
                .some(val => String(val ?? '').toLowerCase().includes(term))
        );
    }, [merchants, buscar]);

    const handleNew = () => {
        navigate("/merchant");
    }

    const handleEdit = (merchant: Merchant) => {
        navigate("/merchant", { state: { merchant } });
    }

    const handleTariff = (merchant: Merchant) => {
        navigate("/merchant/tariffs", { state: { merchant } });
    }

    const handleAccounts = (merchant: Merchant) => {
        navigate("/merchant/accounts", { state: { merchant } });
    }

    const handleTerminals = (merchant: Merchant) => {
        navigate("/merchant/terminals", { state: { merchant } });
    }

    // Ejecuta una acción sobre el comercio mostrando el spinner en su fila
    const runAction = async (merchant: Merchant, action: (id: number) => Promise<string>) => {
        setCurrentMercId(merchant.id);
        const resp = await action(merchant.id);
        if (resp !== "success") {
            setErrorMessage(resp);
        }
        setCurrentMercId(-1);
    }

    const handleActivate = async (merchant: Merchant) => {
        setCurrentMercId(merchant.id);
        const resp = await activateMerchant(merchant.id);
        setCurrentMercId(-1);
        if (typeof resp === 'string') {
            if (resp !== 'success') setErrorMessage(resp);
            return;
        }
        setActivationNotice({ ok: resp.subscription.outcome === 'APPROVED', message: `${merchant.name} quedó activo. ${resp.subscription.message}` });
    };

    const processPendingAction = async () => {
        const action = pendingAction;
        setPendingAction(null);
        if (!action) return;
        await runAction(action.merchant, action.type === 'delete' ? deleteMerchant : suspendMerchant);
    }

    const handleTokenEcom = async (merchant: Merchant) => {
        // Solo el último token funciona: el que tenga el comercio deja de servir
        if (!window.confirm(`¿Generar un token nuevo para "${merchant.name}"? El token actual del comercio dejará de funcionar.`)) return;
        setCurrentMercId(merchant.id);
        const resp = await generateTokenEcom(merchant.id);
        setCurrentMercId(-1);
        if (resp == 'fail') {
            setErrorMessage('Ocurrió un error de comunicación, intente de nuevo.');
        } else {
            setTokenEcom(resp);
        }
    }

    const copyToken = () => {
        navigator.clipboard.writeText(tokenEcom)
            .then(() => showToast("Token copiado al portapapeles"))
            .catch(() => showToast("Error al copiar el token"));
        setTokenEcom('');
    }

    const showToast = (msg: string) => {
        setToast(msg);
        setTimeout(() => setToast(""), 3000);
    };

    const confirmText = pendingAction
        ? pendingAction.type === 'delete'
            ? `¿Está seguro que desea eliminar el comercio "${pendingAction.merchant.name}"? Esta acción no se puede deshacer.`
            : `¿Está seguro que desea suspender el comercio "${pendingAction.merchant.name}"? No podrá procesar transacciones mientras esté suspendido.`
        : '';

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Comercios</h3>
                    <p className="text-muted mb-0">Administre los comercios afiliados, sus tarifas y cuentas de liquidación.</p>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-primary" onClick={handleNew}>
                        <i className="bi bi-plus-lg me-1" /> Agregar comercio
                    </button>
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body">

                    <div className="row g-2 mb-3">
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                <input
                                    className="form-control"
                                    id="search"
                                    type="text"
                                    placeholder="Buscar por nombre, identificación o terminal..."
                                    value={buscar}
                                    onChange={(e) => setBuscar(e.target.value)}
                                />
                                {buscar && (
                                    <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setBuscar('')}>
                                        <i className="bi bi-x-lg" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="table-responsive" style={{ overflow: 'visible' }}>
                        <table id='merchantsTable' className="table table-hover align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Comercio</th>
                                    <th>Banco</th>
                                    <th>Terminal (CRC)</th>
                                    <th>Terminal (USD)</th>
                                    <th>Estado</th>
                                    <th style={{ textAlign: 'center' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoadingPage ? (
                                    <tr><td colSpan={6} className='text-center py-4'>
                                        <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                                    </td></tr>
                                ) : filteredData.length > 0 ? (
                                    filteredData.map(merchant => {
                                        const active = merchant.status == 'ACT';
                                        const busy = merchant.id == currentMercId;
                                        const companyType = getCompanyType(merchant.companyTypeId);
                                        return (
                                            <tr key={merchant.id}>
                                                <td>
                                                    <div className="fw-medium">
                                                        {merchant.name}
                                                        {Number(merchant.branchCount) > 0 && (
                                                            <span className="badge bg-primary-subtle text-primary-emphasis ms-2" title="Casa matriz">
                                                                <i className="bi bi-diagram-3 me-1" />Cadena · {merchant.branchCount} sucursal(es)
                                                            </span>
                                                        )}
                                                    </div>
                                                    {merchant.parent && (
                                                        <div className="small"><i className="bi bi-arrow-return-right me-1" />Sucursal de {merchant.parent.name}</div>
                                                    )}
                                                    <div className="text-muted small">
                                                        {[companyType, merchant.legalId].filter(Boolean).join(' · ')}
                                                    </div>
                                                </td>
                                                <td>{merchant.bank?.description ?? <span className="text-muted">Sin banco</span>}</td>
                                                <td className="text-muted" style={{ fontFamily: 'monospace' }}>{merchant.topId || '-'}</td>
                                                <td className="text-muted" style={{ fontFamily: 'monospace' }}>{merchant.topIdDol || '-'}</td>
                                                <td>
                                                    <span className={`badge rounded-pill ${active ? 'bg-success-subtle text-success-emphasis' : 'bg-secondary-subtle text-secondary-emphasis'}`}>
                                                        {active ? 'Activo' : 'Inactivo'}
                                                    </span>
                                                </td>
                                                <td style={{ textAlign: 'center', width: 130, whiteSpace: 'nowrap' }}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-primary"
                                                        title="Editar comercio"
                                                        disabled={busy}
                                                        onClick={() => handleEdit(merchant)}
                                                    >
                                                        <i className="bi bi-pencil" />
                                                    </button>
                                                    <div className="btn-group ms-1">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary"
                                                            data-bs-toggle="dropdown"
                                                            aria-expanded="false"
                                                            title="Más acciones"
                                                            disabled={busy}
                                                        >
                                                            {busy
                                                                ? <span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14 }} />
                                                                : <i className="bi bi-three-dots-vertical" />}
                                                        </button>
                                                        <ul className="dropdown-menu dropdown-menu-end shadow-sm">
                                                            {/* Tarifas, cuentas y tokens: solo la plataforma */}
                                                            {isPlatform && (<>
                                                            <li>
                                                                <button className="dropdown-item" onClick={() => handleTariff(merchant)}>
                                                                    <i className="bi bi-percent me-2" />Tarifas
                                                                </button>
                                                            </li>
                                                            <li>
                                                                <button className="dropdown-item" onClick={() => handleAccounts(merchant)}>
                                                                    <i className="bi bi-bank me-2" />Cuentas bancarias
                                                                </button>
                                                            </li>
                                                            <li>
                                                                <button className="dropdown-item" onClick={() => handleTokenEcom(merchant)}>
                                                                    <i className="bi bi-key me-2" />Token ecommerce
                                                                </button>
                                                            </li>
                                                            <li><hr className="dropdown-divider" /></li>
                                                            </>)}
                                                            {canManageTerminals && Number(merchant.bankId) === SWIFTPAY_BANK_ID && (<>
                                                            <li>
                                                                <button className="dropdown-item" onClick={() => handleTerminals(merchant)}>
                                                                    <i className="bi bi-credit-card-2-front me-2" />Terminales
                                                                </button>
                                                            </li>
                                                            <li><hr className="dropdown-divider" /></li>
                                                            </>)}
                                                            <li>
                                                                {active ? (
                                                                    <button className="dropdown-item" onClick={() => setPendingAction({ type: 'suspend', merchant })}>
                                                                        <i className="bi bi-pause-circle me-2" />Suspender
                                                                    </button>
                                                                ) : (
                                                                    <button className="dropdown-item" onClick={() => handleActivate(merchant)}>
                                                                        <i className="bi bi-play-circle me-2" />Activar
                                                                    </button>
                                                                )}
                                                            </li>
                                                            <li>
                                                                <button className="dropdown-item text-danger" onClick={() => setPendingAction({ type: 'delete', merchant })}>
                                                                    <i className="bi bi-trash me-2" />Eliminar
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })
                                ) : (
                                    <tr><td colSpan={6} className='text-center py-4 text-muted'>
                                        {buscar ? 'No se encontraron comercios para la búsqueda en esta página.' : 'No existen comercios.'}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-end align-items-center mt-2">
                        {totalPage > 1 ? <Paginator totalPage={totalPage} pageActual={pageActual} onChange={(actual) => setPageActual(actual)} /> : <div />}
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {activationNotice && (
                <Message type={activationNotice.ok ? 'success' : 'error'} title='Comercio activado' message={activationNotice.message}
                    closeDialog={() => setActivationNotice(null)} handleOk={() => setActivationNotice(null)} backgroundColor='rgba(0,0,0,0.4)' />
            )}
            {errorMessage.length > 0 && (
                <Message type='error' message={errorMessage} closeDialog={() => setErrorMessage('')} handleOk={() => setErrorMessage('')} backgroundColor='rgba(0,0,0,0.4)' />
            )}

            {pendingAction && (
                <Message
                    type="success"
                    title="Atención"
                    message={confirmText}
                    closeDialog={() => setPendingAction(null)}
                    showCancel={true}
                    handleCancel={() => setPendingAction(null)}
                    handleOk={processPendingAction}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}

            {tokenEcom.length > 0 && (
                <Message type='success' title='Token para ecommerce' message={tokenEcom} closeDialog={() => setTokenEcom('')}
                    okText='Copiar' handleOk={copyToken} backgroundColor='rgba(0,0,0,0.4)' />
            )}

            {toast && (
                <div className="toast show position-fixed bottom-0 end-0 m-3 align-items-center text-bg-dark border-0" style={{ zIndex: 1100 }}>
                    <div className="toast-body">
                        <i className="bi bi-check-circle me-2" />{toast}
                    </div>
                </div>
            )}
        </div>
    )
}


const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    }
};
